import React, { useRef, useState } from 'react';

// Reducers
import { useAppDispatch } from 'reducers/index';

// Actions
import { getPhotos, updateExistingPhoto } from 'actions/website/entities/PhotosActions';
import { deleteEntityV2 } from 'actions/website/EntityActions';
import type { GenericEntityType } from 'actions/types/website/EntityActionTypes';

// Types
import type { ZolaFileResponseType } from 'actions/types/UploadcareActions';
import type { SharedEntityProps } from 'components/manage/website/entities/EditPageCommon/types';
import { UploadCareTabTypes } from '@zola-helpers/client/dist/es/uploadcare/constants';

// Components
import InputFieldV3 from '@zola/zola-ui/src/components/Form/inputV3/InputFieldV3';
import EditPageDeleteEntityModal from 'components/manage/website/entities/EditPageCommon//EditPageDeleteEntityModal';

import {
  EntityActionButton,
  EntityActionsContainer,
  EntityCaptionContainer,
  EntityContainer,
  EntityDetail,
  EntitySecondaryDetail,
  PhotoEntityCaption,
  StyledDeleteIcon,
  StyledEditIcon,
  StyledEditPhotoUploadcare,
} from './EditPageSortableList.styles';

type EditPageEntityDetailProps = {
  entity: GenericEntityType;
  entityDetailProps: SharedEntityProps;
  isMultiPhoto?: boolean;
  singularItem?: boolean;
  removePhotoFromMultiQueue?: (id: number) => void;
  updatePhotoInMultiQueue?: (id: number, newPhoto: ZolaFileResponseType) => void;
  updatePhotoCaptionInMultiQueue?: (id: number, newCaption: string) => void;
};

const EditPageEntityDetail = ({
  entity,
  entityDetailProps: { onEdit, entityDetail, entitySecondaryDetail, entityEditRemoveName },
  isMultiPhoto,
  singularItem,
  removePhotoFromMultiQueue,
  updatePhotoInMultiQueue,
  updatePhotoCaptionInMultiQueue,
}: EditPageEntityDetailProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const editRef = useRef(null);
  const removeRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);

  let handleEditEntity;
  if (onEdit) {
    handleEditEntity = () => onEdit(entity, editRef);
  }

  const handleToggleModal = () => setModalOpen(!modalOpen);

  const onUploadCompleted = (newPhoto: ZolaFileResponseType) => {
    if (entity && entity.id && updatePhotoInMultiQueue) {
      dispatch(updateExistingPhoto(newPhoto, entity, entity.caption));
      const newPhotoForModal = { ...newPhoto, url: newPhoto.uploadcare_url }; // uploadcare_original_url for unmodified original image, uploadcare_url to retain edits
      updatePhotoInMultiQueue(entity.id, newPhotoForModal);
    }
  };

  const handleImmediateRemove = () => {
    if (entity && entity.id && removePhotoFromMultiQueue) {
      removePhotoFromMultiQueue(entity.id);
      dispatch(deleteEntityV2(entity));
      dispatch(getPhotos());
    }
  };

  const handleCaptionUpdate = (caption: string) => {
    if (entity && entity.id && updatePhotoCaptionInMultiQueue) {
      updatePhotoCaptionInMultiQueue(entity.id, caption);
    }
  };

  if (isMultiPhoto) {
    return (
      <EntityContainer entityEditRemoveName={entityEditRemoveName} isMultiPhoto>
        <EntityDetail singularItem={singularItem}>{entityDetail(entity)}</EntityDetail>
        {entity && entity.id && (
          <EntityCaptionContainer>
            <InputFieldV3
              id={`entity-${entity.id}-caption`}
              value={entity.caption ? entity.caption : ''}
              onChange={e => handleCaptionUpdate(e.currentTarget.value)}
              label="Description"
              isControlled
              placeholder='We first said "I love you" right before our friend snapped this pic of us.'
              maxChars={200}
              errorMessage={
                entity.caption && entity.caption.length > 200 ? 'Caption too long' : undefined
              }
            />
          </EntityCaptionContainer>
        )}
        <EntityActionsContainer entityEditRemoveName={entityEditRemoveName}>
          {entity && entity.image_url && (
            <StyledEditPhotoUploadcare
              editImageUrl={entity.image_url}
              onUploadComplete={zolaFile => {
                onUploadCompleted(zolaFile);
              }}
              effects="mirror,enhance,sharp,blur,grayscale,crop"
              tabs={[UploadCareTabTypes.PREVIEW]}
              analyticsLocation="WEBSITE"
              analyticsProduct="WEBSITE"
              maxSize={20}
              cropRatio="1:1"
            />
          )}
          <EntityActionButton type="button" onClick={handleImmediateRemove}>
            <StyledDeleteIcon />
            Remove photo
          </EntityActionButton>
        </EntityActionsContainer>
      </EntityContainer>
    );
  }

  return (
    <EntityContainer entityEditRemoveName={entityEditRemoveName}>
      <EntityDetail singularItem={singularItem}>{entityDetail(entity)}</EntityDetail>
      <EntityActionsContainer entityEditRemoveName={entityEditRemoveName}>
        <EntityActionButton type="button" ref={editRef} onClick={handleEditEntity}>
          <StyledEditIcon />
          Edit {entityEditRemoveName} details
        </EntityActionButton>
        <EntityActionButton type="button" ref={removeRef} onClick={handleToggleModal}>
          <StyledDeleteIcon />
          Remove {entityEditRemoveName}
        </EntityActionButton>
      </EntityActionsContainer>
      {entity && entity.caption && entityEditRemoveName === 'photo' && (
        <PhotoEntityCaption>{entity.caption}</PhotoEntityCaption>
      )}
      {entitySecondaryDetail && (
        <EntitySecondaryDetail>{entitySecondaryDetail(entity)}</EntitySecondaryDetail>
      )}
      {modalOpen && (
        <EditPageDeleteEntityModal
          onClose={handleToggleModal}
          triggerRef={removeRef}
          entity={entity}
        />
      )}
    </EntityContainer>
  );
};

export default EditPageEntityDetail;
